'use client';

import { c as _c } from "react/compiler-runtime";
import { useAbTestFeatureVariant } from '@sprd/sprd-abtest-next/client';
import { ComboBox, Search } from '@sprd/sprd-component-kit/v2';
import { FocusEvent, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import clsx from 'clsx';
import { useSetAtom } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import { LicenseSuggestion } from '@/types/licenses';
import { ProductTypeSuggestion, TagCategory, TopicSuggestion } from '@/types/searchSuggestions';
import { useSearchSuggestions } from '@/hooks/useSearchSuggestions';
import { HighlightedSuggestion } from '@/components/Header/HighlightedSuggestion';
import { LicenseSuggestionItem } from '@/components/Header/LicenseSuggestionItem';
import { ProductTypeSuggestionItem } from '@/components/Header/ProductTypeSuggestionItem';
import { TagSuggestionItem } from '@/components/Header/TagSuggestionItem';
import { TopicSuggestionItem } from '@/components/Header/TopicSuggestionItem';
import { TrendingSearchesPanel } from '@/components/Header/TrendingSearchesPanel';
import { TrendingLicense, TrendingTerm, resolveTrendingSearchesArm } from '@/components/Header/trendingSearches';
import { usePathname, useRouter } from '@/i18n/navigation';
import { loadingOverlayAtom } from '@/store/atoms';
import { displaySearchTerm } from '@/utility/slugifySearchTerm';
import { normalizeKeyword } from '@/utility/tagKeywordMatch';
import { trackSearchBar, trackSearchBarOpen, trackTrendingSearchesShown } from '@/utility/tracking/trackSearchBar';
import { getAssortmentDetailHrefArgs, getCategorySlug, getMPListPageHrefArgs, getShowroomHrefArgs } from '@/utility/urls';
type ParamValue = string | string[] | undefined;

// Mark ComboBox items that link into the shop so `onChoice` can tell them apart from a search term —
// the callback also fires on plain Enter with the raw input, which must never trigger a navigation.
const LICENSE_VALUE_PREFIX = 'license:';
const PRODUCT_TYPE_VALUE_PREFIX = 'productType:';
const TAG_VALUE_PREFIX = 'tag:';
const CATEGORY_VALUE_PREFIX = 'category:';
const TOPIC_VALUE_PREFIX = 'topic:';
// A tag row without a category of its own: a suggested phrase rather than a link. It carries a
// prefix all the same, so `onChoice` can tell a picked suggestion from the identical text typed by
// hand — picked suggestions are managed terms and keep `/s/`, typed text goes to `?query=`.
const SEARCH_TERM_VALUE_PREFIX = 'searchTerm:';

// Rows that resolve to a link and therefore need a lookup in the current suggestion list.
const SUGGESTION_VALUE_PREFIXES = [LICENSE_VALUE_PREFIX, PRODUCT_TYPE_VALUE_PREFIX, TAG_VALUE_PREFIX, CATEGORY_VALUE_PREFIX, TOPIC_VALUE_PREFIX];
export const SearchWrapper = t0 => {
  const $ = _c(139);
  const {
    className,
    onFocus,
    onBlur,
    onSearch
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = useTranslations();
  const t1 = useAbTestFeatureVariant("trending-searches", "control");
  let t2;
  if ($[0] !== locale || $[1] !== t1) {
    t2 = resolveTrendingSearchesArm(t1, locale);
    $[0] = locale;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const trendingArm = t2;
  const [isPending, startTransition] = useTransition();
  const setLoadingOverlay = useSetAtom(loadingOverlayAtom);
  const [remountCount, setRemountCount] = useState(0);
  const [pendingDisplayTerm, setPendingDisplayTerm] = useState();
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const closeTimer = useRef(undefined);
  const isListPage = pathname === "/shop/[[...slug]]";
  const {
    licenses,
    productTypes,
    tags,
    categoryCompletions,
    topics,
    queryCategory,
    handleChangeDebounced,
    debouncedSearchTerm,
    resetSuggestions
  } = useSearchSuggestions();
  let t3;
  let t4;
  if ($[3] !== isPending || $[4] !== setLoadingOverlay) {
    t3 = () => {
      setLoadingOverlay(isPending);
    };
    t4 = [isPending, setLoadingOverlay];
    $[3] = isPending;
    $[4] = setLoadingOverlay;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== isListPage || $[8] !== params.slug || $[9] !== searchParams) {
    bb0: {
      const freeTextQuery = searchParams.get("query");
      if (freeTextQuery) {
        t5 = displaySearchTerm(freeTextQuery);
        break bb0;
      }
      if (!isListPage || !params.slug || !Array.isArray(params.slug)) {
        t5 = undefined;
        break bb0;
      }
      const slug = params.slug;
      const rawSearchTerm = slug && slug.length > 1 && slug[slug.length - 2] === "s" ? slug[slug.length - 1] : undefined;
      let t6;
      if ($[11] !== rawSearchTerm) {
        t6 = rawSearchTerm ? displaySearchTerm(decodeURIComponent(rawSearchTerm)) : undefined;
        $[11] = rawSearchTerm;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      t5 = t6;
    }
    $[7] = isListPage;
    $[8] = params.slug;
    $[9] = searchParams;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const urlSearchTerm = t5;
  const initialSearchTerm = pendingDisplayTerm ?? urlSearchTerm;
  let t6;
  if ($[13] !== initialSearchTerm) {
    t6 = () => {
      setQuery(initialSearchTerm ?? "");
    };
    $[13] = initialSearchTerm;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== initialSearchTerm || $[16] !== remountCount) {
    t7 = [initialSearchTerm, remountCount];
    $[15] = initialSearchTerm;
    $[16] = remountCount;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[18] !== pendingDisplayTerm || $[19] !== urlSearchTerm) {
    t8 = () => {
      if (pendingDisplayTerm === undefined) {
        return;
      }
      if ((urlSearchTerm ?? "") === pendingDisplayTerm) {
        setPendingDisplayTerm(undefined);
      }
    };
    t9 = [urlSearchTerm, pendingDisplayTerm];
    $[18] = pendingDisplayTerm;
    $[19] = urlSearchTerm;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  if ($[22] !== resetSuggestions) {
    t10 = () => {
      resetSuggestions();
    };
    $[22] = resetSuggestions;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== initialSearchTerm || $[25] !== pathname || $[26] !== resetSuggestions) {
    t11 = [pathname, initialSearchTerm, resetSuggestions];
    $[24] = initialSearchTerm;
    $[25] = pathname;
    $[26] = resetSuggestions;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  useEffect(t10, t11);
  let t12;
  if ($[28] !== onBlur) {
    t12 = () => {
      window.clearTimeout(closeTimer.current);
      setSearchOpen(false);
      onBlur?.();
    };
    $[28] = onBlur;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const closeSearch = t12;
  let t13;
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => () => window.clearTimeout(closeTimer.current);
    t14 = [];
    $[30] = t13;
    $[31] = t14;
  } else {
    t13 = $[30];
    t14 = $[31];
  }
  useEffect(t13, t14);
  let t15;
  if ($[32] !== closeSearch || $[33] !== onSearch || $[34] !== resetSuggestions || $[35] !== router || $[36] !== setLoadingOverlay) {
    t15 = href => {
      if (onSearch) {
        onSearch();
      }
      resetSuggestions();
      closeSearch();
      setRemountCount(_temp);
      setLoadingOverlay(true);
      startTransition(() => {
        router.push(href);
      });
    };
    $[32] = closeSearch;
    $[33] = onSearch;
    $[34] = resetSuggestions;
    $[35] = router;
    $[36] = setLoadingOverlay;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const navigateToSuggestion = t15;
  let t16;
  if ($[38] !== navigateToSuggestion) {
    t16 = license => navigateToSuggestion(getShowroomHrefArgs({
      userSlug: license.slug
    }));
    $[38] = navigateToSuggestion;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const navigateToLicense = t16;
  let t17;
  if ($[40] !== navigateToSuggestion) {
    t17 = userSlug => navigateToSuggestion(getShowroomHrefArgs({
      userSlug
    }));
    $[40] = navigateToSuggestion;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const navigateToLicenseShowroom = t17;
  let t18;
  if ($[42] !== navigateToSuggestion) {
    t18 = productType => navigateToSuggestion(getAssortmentDetailHrefArgs({
      productTypeId: productType.productTypeId,
      productTypeName: productType.name
    }));
    $[42] = navigateToSuggestion;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  const navigateToProductType = t18;
  let t19;
  if ($[44] !== navigateToSuggestion) {
    t19 = category => navigateToSuggestion(getMPListPageHrefArgs({
      categorySlugs: category.slugs,
      searchTerm: category.searchTerm
    }));
    $[44] = navigateToSuggestion;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const navigateToCategory = t19;
  let t20;
  if ($[46] !== navigateToSuggestion) {
    t20 = topic => navigateToSuggestion(getMPListPageHrefArgs({
      categorySlugs: topic.category?.slugs,
      searchTerm: topic.searchTerm
    }));
    $[46] = navigateToSuggestion;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  const navigateToTopic = t20;
  let t21;
  if ($[48] !== categoryCompletions || $[49] !== closeSearch || $[50] !== debouncedSearchTerm || $[51] !== isListPage || $[52] !== licenses || $[53] !== navigateToCategory || $[54] !== navigateToLicense || $[55] !== navigateToLicenseShowroom || $[56] !== navigateToProductType || $[57] !== navigateToSuggestion || $[58] !== navigateToTopic || $[59] !== onSearch || $[60] !== params.slug || $[61] !== productTypes || $[62] !== queryCategory || $[63] !== resetSuggestions || $[64] !== router || $[65] !== searchParams || $[66] !== setLoadingOverlay || $[67] !== tags || $[68] !== topics) {
    t21 = value => {
      if (value.startsWith(LICENSE_VALUE_PREFIX)) {
        const licenseSlug = value.slice(LICENSE_VALUE_PREFIX.length);
        const license_0 = licenses.find(l => l.slug === licenseSlug);
        if (license_0) {
          trackSearchBar(license_0.name, "license");
          navigateToLicense(license_0);
          return;
        }
      }
      if (value.startsWith(PRODUCT_TYPE_VALUE_PREFIX)) {
        const productTypeId = value.slice(PRODUCT_TYPE_VALUE_PREFIX.length);
        const productType_0 = productTypes.find(p => p.productTypeId === productTypeId);
        if (productType_0) {
          trackSearchBar(productType_0.name, "productType");
          navigateToProductType(productType_0);
          return;
        }
      }
      if (value.startsWith(TAG_VALUE_PREFIX)) {
        const expression = value.slice(TAG_VALUE_PREFIX.length);
        const tag = tags.find(t_0 => t_0.expression === expression);
        if (tag?.licenseShowroomSlug) {
          trackSearchBar(expression, "license");
          navigateToLicenseShowroom(tag.licenseShowroomSlug);
          return;
        }
        if (tag?.category) {
          trackSearchBar(expression, "tag");
          navigateToCategory(tag.category);
          return;
        }
      }
      if (value.startsWith(TOPIC_VALUE_PREFIX)) {
        const name = value.slice(TOPIC_VALUE_PREFIX.length);
        const topic_0 = topics.find(candidate => candidate.name === name);
        if (topic_0?.licenseShowroomSlug) {
          trackSearchBar(topic_0.name, "license");
          navigateToLicenseShowroom(topic_0.licenseShowroomSlug);
          return;
        }
        if (topic_0) {
          trackSearchBar(topic_0.name, "topic");
          navigateToTopic(topic_0);
          return;
        }
      }
      if (value.startsWith(CATEGORY_VALUE_PREFIX)) {
        const label = value.slice(CATEGORY_VALUE_PREFIX.length);
        const completion = categoryCompletions.find(c => c.label === label);
        if (completion?.licenseShowroomSlug) {
          trackSearchBar(label, "license");
          navigateToLicenseShowroom(completion.licenseShowroomSlug);
          return;
        }
        if (completion) {
          trackSearchBar(label, "category");
          navigateToCategory(completion.category);
          return;
        }
      }
      const isSuggestedSearchTerm = value.startsWith(SEARCH_TERM_VALUE_PREFIX);
      const term = isSuggestedSearchTerm ? value.slice(SEARCH_TERM_VALUE_PREFIX.length) : value;
      if (isSuggestedSearchTerm) {
        const tag_0 = tags.find(t_1 => t_1.expression === term);
        if (tag_0?.licenseShowroomSlug) {
          trackSearchBar(term, "license");
          navigateToLicenseShowroom(tag_0.licenseShowroomSlug);
          return;
        }
      }
      if (!isSuggestedSearchTerm && queryCategory && normalizeKeyword(term) === normalizeKeyword(debouncedSearchTerm ?? "")) {
        trackSearchBar(term, "typed");
        navigateToSuggestion(getMPListPageHrefArgs({
          categorySlugs: queryCategory.slugs,
          userQuery: term.replaceAll("%", "") || undefined
        }));
        return;
      }
      if (SUGGESTION_VALUE_PREFIXES.some(prefix => value.startsWith(prefix))) {
        return;
      }
      if (onSearch) {
        onSearch();
      }
      const slug_0 = params.slug as Array<string>;
      const categorySlug = isListPage ? getCategorySlug(slug_0) : [];
      const sanitizedSearchTerm = term.replaceAll("%", "");
      trackSearchBar(sanitizedSearchTerm, isSuggestedSearchTerm ? "tag" : "typed");
      resetSuggestions();
      setPendingDisplayTerm(displaySearchTerm(sanitizedSearchTerm));
      closeSearch();
      setRemountCount(_temp2);
      setLoadingOverlay(true);
      startTransition(() => {
        router.push(getMPListPageHrefArgs({
          categorySlugs: categorySlug,
          searchTerm: isSuggestedSearchTerm ? sanitizedSearchTerm : undefined,
          userQuery: !isSuggestedSearchTerm && sanitizedSearchTerm.length ? sanitizedSearchTerm : undefined,
          queryParams: isListPage ? Object.fromEntries([...searchParams.entries()].filter(_temp3)) : undefined
        }));
      });
    };
    $[48] = categoryCompletions;
    $[49] = closeSearch;
    $[50] = debouncedSearchTerm;
    $[51] = isListPage;
    $[52] = licenses;
    $[53] = navigateToCategory;
    $[54] = navigateToLicense;
    $[55] = navigateToLicenseShowroom;
    $[56] = navigateToProductType;
    $[57] = navigateToSuggestion;
    $[58] = navigateToTopic;
    $[59] = onSearch;
    $[60] = params.slug;
    $[61] = productTypes;
    $[62] = queryCategory;
    $[63] = resetSuggestions;
    $[64] = router;
    $[65] = searchParams;
    $[66] = setLoadingOverlay;
    $[67] = tags;
    $[68] = topics;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  const handleChoice = t21;
  let t22;
  if ($[70] !== query) {
    t22 = query.trim();
    $[70] = query;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  const showLiveSuggestions = t22.length > 1;
  let t23;
  if ($[72] !== categoryCompletions || $[73] !== licenses || $[74] !== navigateToCategory || $[75] !== navigateToLicense || $[76] !== navigateToLicenseShowroom || $[77] !== navigateToProductType || $[78] !== navigateToTopic || $[79] !== productTypes || $[80] !== showLiveSuggestions || $[81] !== t || $[82] !== tags || $[83] !== topics) {
    t23 = showLiveSuggestions ? [...licenses.map((license_1, index) => ({
      value: `${LICENSE_VALUE_PREFIX}${license_1.slug}`,
      content: <LicenseSuggestionItem license={license_1} onNavigate={() => {
        trackSearchBar(license_1.name, "license");
        navigateToLicense(license_1);
      }} showGroupLabel={index === 0} showDivider={index === licenses.length - 1 && (!!productTypes.length || !!topics.length || !!categoryCompletions.length || !!tags.length)} />
    })), ...productTypes.map((productType_1, index_0) => ({
      value: `${PRODUCT_TYPE_VALUE_PREFIX}${productType_1.productTypeId}`,
      content: <ProductTypeSuggestionItem productType={productType_1} onNavigate={() => {
        trackSearchBar(productType_1.name, "productType");
        navigateToProductType(productType_1);
      }} showGroupLabel={index_0 === 0} showDivider={index_0 === productTypes.length - 1 && (!!topics.length || !!categoryCompletions.length || !!tags.length)} />
    })), ...topics.map((topic_1, index_1) => ({
      value: `${TOPIC_VALUE_PREFIX}${topic_1.name}`,
      content: <TopicSuggestionItem topic={topic_1} onNavigate={() => {
        if (topic_1.licenseShowroomSlug) {
          trackSearchBar(topic_1.name, "license");
          navigateToLicenseShowroom(topic_1.licenseShowroomSlug);
        } else {
          trackSearchBar(topic_1.name, "topic");
          navigateToTopic(topic_1);
        }
      }} showGroupLabel={index_1 === 0} showDivider={index_1 === topics.length - 1 && !!tags.length} />
    })), ...categoryCompletions.map((t24, index_2) => {
      const {
        label: label_0,
        highlighted,
        category: category_0,
        licenseShowroomSlug
      } = t24;
      return {
        value: `${CATEGORY_VALUE_PREFIX}${label_0}`,
        content: <TagSuggestionItem expression={label_0} highlighted={highlighted} category={category_0} licenseShowroomSlug={licenseShowroomSlug} onNavigate={() => {
          if (licenseShowroomSlug) {
            trackSearchBar(label_0, "license");
            navigateToLicenseShowroom(licenseShowroomSlug);
          } else {
            trackSearchBar(label_0, "category");
            navigateToCategory(category_0);
          }
        }} groupLabel={index_2 === 0 ? t("header.categorySuggestionsLabel") : undefined} showDivider={index_2 === categoryCompletions.length - 1 && !!tags.length} />
      };
    }), ...tags.map(t25 => {
      const {
        expression: expression_0,
        highlighted: highlighted_0,
        category: category_1,
        licenseShowroomSlug: licenseShowroomSlug_0
      } = t25;
      return category_1 ? {
        value: `${TAG_VALUE_PREFIX}${expression_0}`,
        content: <TagSuggestionItem expression={expression_0} highlighted={highlighted_0} category={category_1} licenseShowroomSlug={licenseShowroomSlug_0} onNavigate={() => {
          if (licenseShowroomSlug_0) {
            trackSearchBar(expression_0, "license");
            navigateToLicenseShowroom(licenseShowroomSlug_0);
          } else {
            trackSearchBar(expression_0, "tag");
            navigateToCategory(category_1);
          }
        }} />
      } : {
        value: `${SEARCH_TERM_VALUE_PREFIX}${expression_0}`,
        content: <HighlightedSuggestion>{highlighted_0}</HighlightedSuggestion>
      };
    })] : [];
    $[72] = categoryCompletions;
    $[73] = licenses;
    $[74] = navigateToCategory;
    $[75] = navigateToLicense;
    $[76] = navigateToLicenseShowroom;
    $[77] = navigateToProductType;
    $[78] = navigateToTopic;
    $[79] = productTypes;
    $[80] = showLiveSuggestions;
    $[81] = t;
    $[82] = tags;
    $[83] = topics;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  const comboBoxSuggestions = t23;
  const showTrending = searchOpen && trendingArm !== null && !showLiveSuggestions;
  let t24;
  let t25;
  if ($[85] !== showTrending || $[86] !== trendingArm) {
    t24 = () => {
      if (!showTrending || !trendingArm) {
        return;
      }
      trackTrendingSearchesShown(trendingArm);
    };
    t25 = [showTrending, trendingArm];
    $[85] = showTrending;
    $[86] = trendingArm;
    $[87] = t24;
    $[88] = t25;
  } else {
    t24 = $[87];
    t25 = $[88];
  }
  useEffect(t24, t25);
  let t26;
  if ($[89] !== handleChangeDebounced) {
    t26 = value_0 => {
      setQuery(value_0);
      handleChangeDebounced(value_0);
    };
    $[89] = handleChangeDebounced;
    $[90] = t26;
  } else {
    t26 = $[90];
  }
  const handleValueChange = t26;
  let t27;
  if ($[91] !== onFocus) {
    t27 = () => {
      setSearchOpen(true);
      trackSearchBarOpen();
      onFocus?.();
    };
    $[91] = onFocus;
    $[92] = t27;
  } else {
    t27 = $[92];
  }
  const handleFocus = t27;
  let t28;
  if ($[93] !== onBlur) {
    t28 = event => {
      if (event.currentTarget.contains(event.relatedTarget as Node)) {
        return;
      }
      window.clearTimeout(closeTimer.current);
      closeTimer.current = window.setTimeout(() => {
        setSearchOpen(false);
        onBlur?.();
      }, 0);
    };
    $[93] = onBlur;
    $[94] = t28;
  } else {
    t28 = $[94];
  }
  const handleWrapperBlur = t28;
  let t29;
  if ($[95] !== handleChoice || $[96] !== resetSuggestions || $[97] !== trendingArm) {
    t29 = () => {
      window.clearTimeout(closeTimer.current);
      setQuery("");
      if (trendingArm) {
        resetSuggestions();
        setSearchOpen(true);
        return;
      }
      handleChoice("");
    };
    $[95] = handleChoice;
    $[96] = resetSuggestions;
    $[97] = trendingArm;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  const handleClear = t29;
  let t30;
  if ($[99] !== navigateToSuggestion) {
    t30 = (term_0, categorySlugs) => {
      trackSearchBar(term_0.searchTerm, "trending");
      navigateToSuggestion(getMPListPageHrefArgs({
        categorySlugs,
        searchTerm: term_0.searchTerm
      }));
    };
    $[99] = navigateToSuggestion;
    $[100] = t30;
  } else {
    t30 = $[100];
  }
  const openTrendingSearch = t30;
  let t31;
  if ($[101] !== navigateToLicense) {
    t31 = license_2 => {
      trackSearchBar(license_2.name, "popularLicense");
      navigateToLicense(license_2);
    };
    $[101] = navigateToLicense;
    $[102] = t31;
  } else {
    t31 = $[102];
  }
  const openPopularLicense = t31;
  let t32;
  if ($[103] !== className) {
    t32 = clsx("relative z-1 mr-2 grow", className);
    $[103] = className;
    $[104] = t32;
  } else {
    t32 = $[104];
  }
  let t33;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = () => {
      window.clearTimeout(closeTimer.current);
      setSearchOpen(true);
    };
    $[105] = t33;
  } else {
    t33 = $[105];
  }
  const t34 = `${pathname}-${initialSearchTerm ?? ""}-${remountCount}`;
  let t35;
  if ($[106] !== t) {
    t35 = t("general.a11y.submitSearch");
    $[106] = t;
    $[107] = t35;
  } else {
    t35 = $[107];
  }
  let t36;
  if ($[108] !== t35) {
    t36 = <Search className="size-6" aria-label={t35} />;
    $[108] = t35;
    $[109] = t36;
  } else {
    t36 = $[109];
  }
  let t37;
  if ($[110] !== t) {
    t37 = t("general.search");
    $[110] = t;
    $[111] = t37;
  } else {
    t37 = $[111];
  }
  let t38;
  if ($[112] !== t) {
    t38 = t("general.search");
    $[112] = t;
    $[113] = t38;
  } else {
    t38 = $[113];
  }
  let t39;
  if ($[114] !== t) {
    t39 = t("general.a11y.clearSearch");
    $[114] = t;
    $[115] = t39;
  } else {
    t39 = $[115];
  }
  let t40;
  if ($[116] !== comboBoxSuggestions || $[117] !== handleChoice || $[118] !== handleClear || $[119] !== handleFocus || $[120] !== handleValueChange || $[121] !== initialSearchTerm || $[122] !== t34 || $[123] !== t36 || $[124] !== t37 || $[125] !== t38 || $[126] !== t39) {
    t40 = <ComboBox key={t34} className="w-full [&>ul]:max-h-[calc(var(--visual-viewport-height)-var(--header-height)-var(--header-banner-height)-5rem)] [&>ul]:overflow-y-auto [&>ul]:overscroll-contain" icon={t36} label={t37} placeholder={t38} initialValue={initialSearchTerm} onValueChange={handleValueChange} onChoice={handleChoice} onFocus={handleFocus} clearLabel={t39} onClear={handleClear} suggestions={comboBoxSuggestions} />;
    $[116] = comboBoxSuggestions;
    $[117] = handleChoice;
    $[118] = handleClear;
    $[119] = handleFocus;
    $[120] = handleValueChange;
    $[121] = initialSearchTerm;
    $[122] = t34;
    $[123] = t36;
    $[124] = t37;
    $[125] = t38;
    $[126] = t39;
    $[127] = t40;
  } else {
    t40 = $[127];
  }
  let t41;
  if ($[128] !== locale || $[129] !== openPopularLicense || $[130] !== openTrendingSearch || $[131] !== showTrending || $[132] !== trendingArm) {
    t41 = showTrending && trendingArm && <TrendingSearchesPanel arm={trendingArm} locale={locale} onTrendingSearch={openTrendingSearch} onLicense={openPopularLicense} />;
    $[128] = locale;
    $[129] = openPopularLicense;
    $[130] = openTrendingSearch;
    $[131] = showTrending;
    $[132] = trendingArm;
    $[133] = t41;
  } else {
    t41 = $[133];
  }
  let t42;
  if ($[134] !== handleWrapperBlur || $[135] !== t32 || $[136] !== t40 || $[137] !== t41) {
    t42 = <div className={t32} onFocus={t33} onBlur={handleWrapperBlur}>{t40}{t41}</div>;
    $[134] = handleWrapperBlur;
    $[135] = t32;
    $[136] = t40;
    $[137] = t41;
    $[138] = t42;
  } else {
    t42 = $[138];
  }
  return t42;
};
function _temp(count) {
  return count + 1;
}
function _temp2(count_0) {
  return count_0 + 1;
}
function _temp3(e) {
  return e[0] !== "page" && e[0] !== "query";
}